import { createServerFn } from "@tanstack/react-start";
import { createClient } from "@supabase/supabase-js";
import type { ApprovedTheme } from "@/lib/themes-public";

/** Approved (editor-confirmed) themes only, read with the public key so RLS limits it to approved rows. */
export const listApprovedThemes = createServerFn({ method: "GET" }).handler(async (): Promise<ApprovedTheme[]> => {
  const key = process.env.SUPABASE_PUBLISHABLE_KEY!;
  const sb = createClient(process.env.SUPABASE_URL!, key, {
    auth: { storage: undefined, persistSession: false, autoRefreshToken: false },
    global: {
      fetch: (input, init) => {
        const h = new Headers(init?.headers);
        if (key.startsWith("sb_")) h.delete("Authorization");
        h.set("apikey", key);
        return fetch(input, { ...init, headers: h });
      },
    },
  });
  const [{ data: rev, error: e1 }, { data: posts, error: e2 }] = await Promise.all([
    sb.from("post_theme_reviews").select("post_id, approved_primary, approved_secondary, status").eq("status", "onayli"),
    sb.from("posts").select("id, slug").eq("published", true),
  ]);
  if (e1) throw new Error(e1.message);
  if (e2) throw new Error(e2.message);
  const slugById = new Map((posts ?? []).map((p) => [p.id, p.slug]));
  return (rev ?? [])
    .filter((r) => r.approved_primary && slugById.has(r.post_id))
    .map((r) => ({
      slug: slugById.get(r.post_id)!,
      primary: r.approved_primary as ApprovedTheme["primary"],
      secondary: (r.approved_secondary as ApprovedTheme["secondary"]) ?? null,
    }));
});
